// frontend/src/pages/Dashboard.tsx
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../context/userContext';
import { Heading, Subheading } from '../../components/Heading';
import { Select } from '../../components/Select';
import { useWarehouseProduct } from '../../hooks/useWarehouseProduct';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/Table';
import dateFormat from 'dateformat';
import { WarehouseProduct } from '../../models/WarehouseProduct';
import { useSetupUsergroup } from '../../hooks/useSetupUsergroup';
import { WarehouseProductItemMovement } from '../../models/WarehouseProductItemMovement';
import { WarehouseProductItemBatchNoMovement } from '../../models/WarehouseProductItemBatchNoMovement';
import useWarehouseProductItemMovement from '../../hooks/useWarehouseProductItemMovement';
import useWarehouseProductItemBatchNoMovement from '../../hooks/useWarehouseProductItemBatchNoMovement';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import {
    ShoppingBagIcon,
    TruckIcon,
    ExclamationTriangleIcon,
    DevicePhoneMobileIcon,
    UserGroupIcon,
    ClipboardDocumentCheckIcon,
    CalendarDaysIcon,
    CubeIcon,
    Square3Stack3DIcon,
} from '@heroicons/react/16/solid';
import { StatCard, HighlightedStatCard } from '../../components/StatCard';
import { ChartSkeleton, TableSkeletonRows } from '../../components/Skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/Tabs';
import { useTrackingOrder } from '../../hooks/useTrackingOrder';
import { useAppTrackCheckIns } from '../../hooks/useAppTrackCheckIn';
import { useAppUser } from '../../hooks/useAppUser';
import { useAppPreCallPlans } from '../../hooks/useAppPreCallPlan';
import { useAppPostCallPlans } from '../../hooks/useAppPostCallPlan';
import { PreCallPlan } from '../../models/AppPreCallPlan';
import { PostCallPlan } from '../../models/AppPostCallPlan';
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

const SEVERITY_COLORS = { critical: '#ba1a1a', low: '#92400e', nearThreshold: '#9fe4fd' };
const STATUS_PALETTE = ['#006565', '#15677c', '#9fe4fd', '#166534', '#ba1a1a', '#8b4823', '#92400e', '#0369a1'];

function isToday(dateStr: string) {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function buildSeverityData(products: WarehouseProduct[]) {
    let critical = 0;
    let low = 0;
    let nearThreshold = 0;
    products.forEach((p) => {
        const qty = Number(p.current_quantity ?? 0);
        const min = Number(p.minimum_quantity ?? 0);
        if (min <= 0) {
            nearThreshold += 1;
            return;
        }
        const ratio = qty / min;
        if (ratio <= 0.25) critical += 1;
        else if (ratio <= 0.75) low += 1;
        else nearThreshold += 1;
    });
    return [
        { name: 'Critical', value: critical, color: SEVERITY_COLORS.critical },
        { name: 'Low', value: low, color: SEVERITY_COLORS.low },
        { name: 'Near Threshold', value: nearThreshold, color: SEVERITY_COLORS.nearThreshold },
    ];
}

function buildStatusBreakdown(orders: { status: string }[]) {
    const counts = new Map<string, number>();
    orders.forEach((o) => {
        const key = o.status || 'Unknown';
        counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([name, value], i) => ({
        name,
        value,
        color: STATUS_PALETTE[i % STATUS_PALETTE.length],
    }));
}

// Get current hour to set greeting message
const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return 'Good morning';
    if (currentHour < 18) return 'Good afternoon';
    return 'Good evening';
};

function SupplierDashboard() {
    const { currentUser } = useContext(UserContext);
    const { fetchSupplierByUserId } = useSetupUsergroup();
    const { movements, loading: movementsLoading } = useWarehouseProductItemMovement();
    const { batchNoMovements, loading: batchNoMovementsLoading } = useWarehouseProductItemBatchNoMovement();
    const [period, setPeriod] = useState<string>('last week');

    useEffect(() => {
        document.title = "Dashboard";
    }, []);

    // Helper function to filter by selected period
    const filterByPeriod = (data: any[], period: string) => {
        const now = new Date();
        const startDate = new Date();

        if (period === 'last week') startDate.setDate(now.getDate() - 7);
        else if (period === 'last two weeks') startDate.setDate(now.getDate() - 14);
        else if (period === 'last month') startDate.setMonth(now.getMonth() - 1);

        return data.filter(item => new Date(item.date) >= startDate);
    };

    // Calculate changes
    const calculateChange = (data: any[], period: string) => {
        const filteredData = filterByPeriod(data, period);
        const currentPeriodCount = filteredData.length;
        const previousPeriodCount = data.length - currentPeriodCount;

        if (previousPeriodCount === 0) return '0%';

        const change = ((currentPeriodCount - previousPeriodCount) / previousPeriodCount) * 100;
        return `${change > 0 ? '+' : ''}${change.toFixed(2)}%`;
    };

    return (
        <div>
            <Heading>{getGreeting()}, {currentUser ? currentUser.fullname : "Guest"}</Heading>
            <div className="mt-8 flex flex-wrap items-end justify-between gap-3">
                <Subheading>Supplier Dashboard</Subheading>
                <div>
                    <Select
                        name="period"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)} // Update period state
                    >
                        <option value="last week">Last week</option>
                        <option value="last two weeks">Last two weeks</option>
                        <option value="last month">Last month</option>
                    </Select>
                </div>
            </div>
            <div className="mt-4 grid gap-8 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard title="Stock Movement" value={movements.length.toString() || '0'} change={calculateChange(movements, period)} period={period} loading={movementsLoading} />
                <StatCard title="Stock By Batch" value={batchNoMovements.length.toString() || '0'} change={calculateChange(batchNoMovements, period)} period={period} loading={batchNoMovementsLoading} />
            </div>
        </div>
    )
}

function InventoryTab() {
    const { stockRemindProducts, loading: stockRemindLoading, productSummary, productSummaryLoading } = useWarehouseProduct();
    const severityData = buildSeverityData(stockRemindProducts);

    return (
        <div>
            <Subheading>Inventory</Subheading>

            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    title="Total Products"
                    value={(productSummary?.totalProducts ?? 0).toString()}
                    icon={<CubeIcon className="size-5" />}
                    loading={productSummaryLoading}
                />
                <StatCard
                    title="Total Stock Quantity"
                    value={(productSummary?.totalQuantity ?? 0).toLocaleString()}
                    icon={<Square3Stack3DIcon className="size-5" />}
                    loading={productSummaryLoading}
                />
                <StatCard
                    title="Out of Stock"
                    value={(productSummary?.outOfStockCount ?? 0).toString()}
                    icon={<ExclamationTriangleIcon className="size-5" />}
                    loading={productSummaryLoading}
                />
                <StatCard
                    title="Low Stock Alerts"
                    value={stockRemindProducts.length.toString() || '0'}
                    icon={<ShoppingBagIcon className="size-5" />}
                    loading={stockRemindLoading}
                />
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={() => window.location.assign('/gp/inventories/products/add')}>
                    Add Product
                </Button>
            </div>

            {!productSummaryLoading && productSummary && (
                <div className="mt-10 rounded-2xl bg-card p-5 shadow-sm">
                    <Subheading>Products by Category</Subheading>
                    {productSummary.byCategory.length > 0 ? (
                        <ul className="mt-3 divide-y divide-zinc-950/5 dark:divide-white/5">
                            {productSummary.byCategory.map((c) => (
                                <li key={c.categoryName} className="flex items-center justify-between gap-3 py-2 text-sm">
                                    <span className="text-card-foreground">{c.categoryName}</span>
                                    <Badge color="zinc">{c.count}</Badge>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-3 text-sm text-zinc-500">No category data yet. Products are missing a category - assign one from the product edit page.</p>
                    )}
                </div>
            )}

            <div className="mt-10 rounded-2xl bg-card p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <Subheading>Low Stock Severity</Subheading>
                    <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
                        {stockRemindProducts.length} products
                    </span>
                </div>
                <div className="mt-4 h-64">
                    {stockRemindLoading ? (
                        <ChartSkeleton />
                    ) : stockRemindProducts.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-zinc-500">
                            No low stock items right now.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={severityData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={55}
                                    outerRadius={85}
                                    paddingAngle={2}
                                >
                                    {severityData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className="mt-10 rounded-2xl bg-card p-5 shadow-sm">
                <Subheading>Products Needing Reorder</Subheading>
                <p className="mt-1 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                    These products are below their minimum quantity. Please reorder as necessary.
                </p>
                <Table className="mt-6 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                    <TableHead>
                        <TableRow>
                            <TableHeader>Product Item No</TableHeader>
                            <TableHeader>Product Second Item No</TableHeader>
                            <TableHeader>Description</TableHeader>
                            <TableHeader>Minimum Quantity</TableHeader>
                            <TableHeader className="text-right">Current Quantity</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stockRemindLoading ? (
                            <TableSkeletonRows rows={3} columns={5} />
                        ) : (stockRemindProducts.length > 0) ? stockRemindProducts.map((product) => (
                            <TableRow key={product.id} title={`${product.item_no}`}>
                                <TableCell >{product.item_no}</TableCell>
                                <TableCell className="text-zinc-500">{product.second_item_no || '-'}</TableCell>
                                <TableCell className="text-zinc-500">{product.description || '-'}</TableCell>
                                <TableCell className="text-zinc-500">{product.minimum_quantity}</TableCell>
                                <TableCell className="text-red-500 text-right">{product.current_quantity}</TableCell>
                            </TableRow>
                        )) : <TableRow>
                            <TableCell colSpan={9} className="text-center py-8">No product found.</TableCell>
                        </TableRow>}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

function TrackingTab() {
    const { trackingOrders, staleOrders, loading } = useTrackingOrder();
    const statusBreakdown = buildStatusBreakdown(trackingOrders);

    return (
        <div>
            <Subheading>Tracking</Subheading>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <StatCard title="Total Tracking Orders" value={trackingOrders.length.toString()} icon={<TruckIcon className="size-5" />} loading={loading} />
                <HighlightedStatCard
                    title="Stale / No Update"
                    value={staleOrders.length.toString()}
                    caption="Orders with no delivery-man update"
                    icon={<ExclamationTriangleIcon className="size-5 text-white" />}
                    loading={loading}
                />
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl bg-card p-5 shadow-sm">
                    <Subheading>Status Breakdown</Subheading>
                    <div className="mt-4 h-64">
                        {loading ? (
                            <ChartSkeleton />
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={statusBreakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                                        {statusBreakdown.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="rounded-2xl bg-card p-5 shadow-sm">
                    <Subheading>Recent Tracking Orders</Subheading>
                    <Table className="mt-4 [--gutter:theme(spacing.4)]">
                        <TableHead>
                            <TableRow>
                                <TableHeader>Reference No</TableHeader>
                                <TableHeader>Customer</TableHeader>
                                <TableHeader>Status</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableSkeletonRows rows={5} columns={3} />
                            ) : trackingOrders.length > 0 ? trackingOrders.slice(0, 5).map((order) => (
                                <TableRow key={order.id} title={order.reference_no}>
                                    <TableCell>{order.reference_no}</TableCell>
                                    <TableCell className="text-zinc-500">{order.customer_fullname || '-'}</TableCell>
                                    <TableCell><Badge color="blue">{order.status}</Badge></TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-8">No tracking orders found.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}

function MobileAppTab() {
    const { trackCheckIns, loading: checkInsLoading } = useAppTrackCheckIns();
    const { appUsers, loading: appUsersLoading } = useAppUser();
    const { fetchPreCallPlansForToday, loading: preCallLoadingHook } = useAppPreCallPlans();
    const { fetchPostCallPlansForToday, loading: postCallLoadingHook } = useAppPostCallPlans();
    const [preCallToday, setPreCallToday] = useState<PreCallPlan[]>([]);
    const [postCallToday, setPostCallToday] = useState<PostCallPlan[]>([]);
    const [callPlansLoading, setCallPlansLoading] = useState<boolean>(true);

    useEffect(() => {
        (async () => {
            setCallPlansLoading(true);
            try {
                const [pre, post] = await Promise.all([fetchPreCallPlansForToday(), fetchPostCallPlansForToday()]);
                setPreCallToday(pre || []);
                setPostCallToday(post || []);
            } catch (error) {
                console.error('Failed to fetch today\'s call plans:', error);
            } finally {
                setCallPlansLoading(false);
            }
        })();
    }, []);

    const checkInsToday = trackCheckIns.filter((c) => isToday(c.created_time)).length;

    return (
        <div>
            <Subheading>Mobile App</Subheading>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard title="Check-ins Today" value={checkInsToday.toString()} icon={<CalendarDaysIcon className="size-5" />} loading={checkInsLoading} />
                <StatCard title="Active App Users" value={appUsers.length.toString()} icon={<UserGroupIcon className="size-5" />} loading={appUsersLoading} />
                <StatCard title="Pre-Call Plans Today" value={preCallToday.length.toString()} icon={<ClipboardDocumentCheckIcon className="size-5" />} loading={callPlansLoading || preCallLoadingHook} />
                <StatCard title="Post-Call Plans Today" value={postCallToday.length.toString()} icon={<DevicePhoneMobileIcon className="size-5" />} loading={callPlansLoading || postCallLoadingHook} />
            </div>

            <div className="mt-10 rounded-2xl bg-card p-5 shadow-sm">
                <Subheading>Recent Check-Ins</Subheading>
                <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                    <TableHead>
                        <TableRow>
                            <TableHeader>Salesman</TableHeader>
                            <TableHeader>Location</TableHeader>
                            <TableHeader className="text-right">Check-in Time</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {checkInsLoading ? (
                            <TableSkeletonRows rows={5} columns={3} />
                        ) : trackCheckIns.length > 0 ? trackCheckIns.slice(0, 5).map((checkIn) => (
                            <TableRow key={checkIn.id}>
                                <TableCell>{checkIn.salesman_username || '-'}</TableCell>
                                <TableCell className="text-zinc-500">{checkIn.location_name || '-'}</TableCell>
                                <TableCell className="text-zinc-400 text-right">{dateFormat(checkIn.created_time, "mmmm dd, yyyy hh:MM TT")}</TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={3} className="text-center py-8">No check-ins found.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

const GHDashboard: React.FC = () => {
    const { currentUser } = useContext(UserContext);

    useEffect(() => {
        document.title = "Dashboard";
    }, []);

    if (currentUser?.role.toString() === "5") return <SupplierDashboard />;

    return (
        <>
            <Heading>{getGreeting()}, {currentUser ? currentUser.fullname : "Guest"}</Heading>
            <div className="mt-6">
                <Tabs defaultValue="inventory">
                    <TabsList>
                        <TabsTrigger value="inventory">Inventory</TabsTrigger>
                        <TabsTrigger value="tracking">Tracking</TabsTrigger>
                        <TabsTrigger value="mobile-app">Mobile App</TabsTrigger>
                    </TabsList>
                    <TabsContent value="inventory">
                        <InventoryTab />
                    </TabsContent>
                    <TabsContent value="tracking">
                        <TrackingTab />
                    </TabsContent>
                    <TabsContent value="mobile-app">
                        <MobileAppTab />
                    </TabsContent>
                </Tabs>
            </div>
        </>
    );
};

export default GHDashboard;
